import React, { useContext, useState, useEffect, createContext } from 'react';
import { DeclaredComponent } from './DynamicComponent';
import { Spin, Button, Row, Col, Card, Modal } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  navigate,
  toAbsPath,
  navigateToHome,
  navigateWithData,
} from '../navigation/navigationHistory';
import Divider from 'antd/lib/divider';
import { consumePath, NavigationContext } from '../navigation/Navigation';
import { ErrorScope } from './ErrorScope';
import { FormValidationContext } from '../context/FormValidationProvider';
import { StepsValidationStatusContext } from '../context/StepsValidationStatusProvider';
import { PremiumAlertMessageContext } from '../context/PremiumAlertMessageProvider';
import { WorkflowStepsContext } from '../context/WorkflowStepsProvider';
import ProgressBar from './ProgressBar';
import { addBean } from './forms/AddBeanBtn';
import { ModelContext } from './metadata/ModelSource';
import Alert from 'antd/lib/alert';
import { GenericButton } from '../components/fields/GenericButton';
import { api } from '../api';
import { env } from '../env';
import {
  postApplicationDownPayment,
  updatePaymentPlansAndBuy,
  buyBindRequest,
  driverDataReportRequest
} from '../api/buyAPI';
import { onSubmitAgent, putApplication, getApplication } from '../api/applicationAPI';
import { BuyNextButton } from './BuyNextButton';
import { paymentRequest } from '../api/buyAPI';
import { GlobalStateContext } from '../context/GlobalStateProvider';
import HorizontalProgressBar from './HorizontalProgressBar';
import { Summary } from './Summary';
import { InlineNotification } from './fields/InlineNotification';
import {ApplicationActionsContext} from './data/ApplicationDataSource';
import {ConfigContext} from 'portal-core/components/PortalApp';
import {GA_CONFIGURATION, sendGoogleAnalyticsEvent} from "../utils/googleAnalyticsUtils";
import {ActionDescription} from "./forms/policy/SummaryDetails";
import {DriversAndVehiclesDataReportContext} from "../context/DriversAndVehiclesDataReportProvider";

const initialBuyContext = {
  state: {},
  setState: () => {},
  paymentRequest: () => {},
  navigateToStep: () => {},
  currentStep: '',
  isValid: '',
  next: () => {},
  moveTo: () => {},
  steps: [],
  bindAndNavigate: () => {},
  mainAction: () => {},
};

export const BuyContext = createContext(initialBuyContext);

export function Workflow({ lineCd, riskTypeCd, steps, dependencies, onMove, endFlowInd, ...props }) {
  const navigation = useContext(NavigationContext);
  const { setSteps, setCurrentStep } = useContext(StepsValidationStatusContext);
  const { disabledSteps, setDisabledSteps } = useContext(WorkflowStepsContext);
  const { showAlert, setShowAlert, message } = useContext(PremiumAlertMessageContext);
  const { isDesktop, isTablet, isMobile, hideNavLabels } = useContext(
    GlobalStateContext
  );
  const {pullApplication, saveApplication} = useContext(ApplicationActionsContext)

  const [subNavigation, [stepKey]] = consumePath(navigation);
  const currentStep = Math.max(
    steps.findIndex((s) => s.key === stepKey),
    0
  );

  const config = useContext(ConfigContext);
  const emailQuoteText = config.branding.buttons.emailQuote ? config.branding.buttons.emailQuote : 'Email Quote';
  const referAgentText = config.branding.buttons.referAgent ? config.branding.buttons.referAgent : 'Request Help';
  const quoteAdvanceText = config.branding.buttons.quoteAdvance ? config.branding.buttons.quoteAdvance : 'Buy';
  const applicationAdvanceText = config.branding.buttons.applicationAdvance ? config.branding.buttons.applicationAdvance : 'Buy Now';
  const applicationReferText = config.branding.buttons.applicationRefer ? config.branding.buttons.applicationRefer : 'Apply';

  const model = useContext(ModelContext);
  useEffect(() => {
    setSteps(steps);
  }, [steps]);

  useEffect(() => {
    setCurrentStep(currentStep);
    const step = steps[currentStep];

    const path = step.key;
    if (path !== 'drivers' || path !== 'vehicles') {
      scrollTo(0, 0);
    }
    if (path === 'drivers' && !model.data.drivers) {
      // Opens up a new driver form when visiting the first time
      const maritalStatusCd = model.getValue('insured.partyInfo[0].personInfo.maritalStatusCd');
      const givenName = model.getValue('insured.partyInfo[0].nameInfo.givenName');
      let otherGivenName = model.getValue('insured.partyInfo[0].nameInfo.otherGivenName');
      let suffixCd = model.getValue('insured.partyInfo[0].nameInfo.suffixCd');
      const surname = model.getValue('insured.partyInfo[0].nameInfo.surname');
      const birthDt = model.getValue('insured.partyInfo[0].personInfo.birthDt');
      if (otherGivenName === undefined) {
      	otherGivenName = "";
      }
      if (suffixCd === undefined) {
      	suffixCd = "";
      }
      const proto = {
        partyTypeCd: 'DriverParty',
        personInfo: {maritalStatusCd: `${maritalStatusCd}`, birthDt: `${birthDt}`},
        nameInfo: {givenName: `${givenName}`, otherGivenName: `${otherGivenName}`, surname: `${surname}`, suffixCd: `${suffixCd}`},
        driverInfo: {relationshipToInsuredCd: `Self`}
      };
      addBean(path, proto, model);
    }
    if (path === 'vehicles' && !model.data.lines[0].risks) {
      // Opens up a new vehicle form when visiting the first time
      const proto = {
        typeCd: `${riskTypeCd}`,
      };
      const vehiclePath = `lines{lineCd:${lineCd}}.risks`;
      addBean(vehiclePath, proto, model);
    }

    const commercialName = model.getValue(
      'insured.partyInfo[0].nameInfo.commercialName'
    );
    // setting commercial name here since it is a hidden field from the user
    if (currentStep !== 0 && !commercialName) {
      const insured = model.data.insured.partyInfo.filter((party) => {
        return party.partyTypeCd === 'InsuredParty';
      });

      const givenName = insured[0].nameInfo.givenName;
      const surname = insured[0].nameInfo.surname;
      const combinedName = `${surname}, ${givenName}`;
      model.setValue(
        'insured.partyInfo[0].nameInfo.commercialName',
          combinedName
      );

      //if commericalName is not set then indexName isn't set either
      model.setValue('insured.indexName', combinedName);
    }
  }, [currentStep]);

  function navigateToStep(step) {
    const key = steps[step].key;
    navigate(toAbsPath(key, navigation));
  }

  const [state, setState] = useState({
    completedMask: 0,
    loadingNext: false,
    loadingBack: false,
    inactive: false,
    emailQuoteErrors: null,
    loadingEmail: false,
    loadingCancel: false,
    emailNotificationID: 0,
    paymentLoading: false,
  });

  function moveTo(toStep, loadingNext, loadingBack) {
    const dir = toStep - currentStep;
    if (dir === 0) {
      return;
    }

    model.data.basicPolicy.comments =  steps[toStep].title;

    const weakTransition =
      (dir > 0 && steps[toStep].weak) || (dir < 0 && steps[currentStep].weak);
    if (weakTransition) {
      navigateToStep(toStep);
      return;
    }

    setState({
      ...state,
      inactive: true,
      loadingNext,
      loadingBack,
      emailQuoteErrors: null,
    });

    const unlockUI = () =>
      setState({
        ...state,
        inactive: false,
        loadingNext: false,
        loadingBack: false,
        emailQuoteErrors: null,
      });

    onMove(currentStep, toStep, steps)
      .then(() => {
        navigateToStep(toStep);
        unlockUI();
      })
      .catch(unlockUI);
  }

  function next() {
    const toStep = Math.min(steps.length - 1, currentStep + 1);
    moveTo(toStep, true, false);
  }

  function back() {
    let toStep = Math.max(0, currentStep - 1);
    if (disabledSteps.includes(steps[toStep].key)){
      toStep = Math.max(0, toStep - 1);
    }
    moveTo(toStep, false, true);
  }

  const emailAddrPath = 'insured.partyInfo[0].emailInfo.emailAddr';
  const emailAddr = model.getValue(emailAddrPath);
  
  const givenNamePath = 'insured.partyInfo[0].nameInfo.givenName';
  const givenName = model.getValue(givenNamePath);
  
  const surnamePath = 'insured.partyInfo[0].nameInfo.surname';
  const surname = model.getValue(surnamePath);

  async function onSubmitEmail() {
    sendGoogleAnalyticsEvent({...GA_CONFIGURATION.Email_Quote,event_label:window.location.pathname});
    const resourceId = model.data.systemId;
    setState({
      loadingEmail: true,
      emailNotificationID: state.emailNotificationID + 1,
    });
    try {
      const emailResponse = await api.post(
        `/applications/${resourceId}/consumerEmailRequest`
      );
      if (emailResponse.status === 200 || emailResponse.status === 201) {
        model.notify({
          id: 'emailSentSuccess' + state.emailNotificationID,
          message: `Email has been sent to ${emailAddr} successfully`,
          type: 'success',
          overrideExisting: true,
        });
        setState({
          loadingEmail: false,
          emailNotificationID: state.emailNotificationID + 1,
        });
      } else if (emailResponse.status === 'error') {
        setState({
          loadingEmail: false,
          emailNotificationID: state.emailNotificationID + 1,
        });
        throw new Error('Email response returned error');
      }
    } catch (err) {
      model.notify({
        id: 'emailSentFailure' + state.emailNotificationID,
        message: `We were unable to send an email to ${emailAddr} successfully`,
        type: 'error',
        overrideExisting: true,
      });
      setState({
        loadingEmail: false,
      });
    }
  }

  const disabled = state.inactive;
  const step = steps[currentStep];

  const { isValid, formStatuses } = useContext(FormValidationContext);

  const currentNavStep = steps[currentStep];

  const phoneNumberAttr = 'insured.partyInfo[0].phoneInfo[0].phoneNumber';
  const defaultPhoneNumber = model.getValue(phoneNumberAttr);

  const shouldDisplayAgentButton = (emailAddr || defaultPhoneNumber)
    && step.key === "quote";
  const shouldDisplayEmail =
    (model.data.typeCd !== 'QuickQuote' ||
    (model.data.typeCd === 'QuickQuote' && step.key !== 'general'))
    && step.key === "quote";

  const antIcon = <LoadingOutlined style={{ fontSize: '38px' }} spin />;
  const resourceId = model.data.systemId;
  const applicationNumber = model.data.applicationNumber;

  const onCancelClick = () => {
    setState({
      loadingCancel: true,
    });
    setTimeout(() => {
      setState({ loadingCancel: false });
      navigateToHome();
    }, 1000);
  };

  const bindAndNavigate = async () => {
    setState({
      loadingBack: true,
      loadingNext: true,
    });

    const lastStep = steps.length - 1;

    await buyBindRequest(
      model.data.systemId,
      model.data.applicationNumber,
      navigate,
      navigateToStep,
      lastStep,
      setState,
      pullApplication
    );
  };

  const driverDataReportNavigate = async () => {
    setState({
      loadingBack: true,
      loadingNext: true,
    });

    await saveApplication();

    const toStep = Math.min(steps.length - 1, currentStep + 1);
    const toEmptyStep = Math.min(steps.length - 1, currentStep + 2);

    await driverDataReportRequest(
        model.data.systemId,
        model.data.applicationNumber,
        navigate,
        navigateToStep,
        toStep,
        toEmptyStep,
        setState,
        pullApplication,
        disabledSteps,
        setDisabledSteps
    );
  };

  let mainAction = () => {};
  const mainNavButton = (advanceText, onClick, id=advanceText) => {
    mainAction = onClick;
    return (
        <Button
            style={{ marginLeft: '4px' }}
            type="primary"
            id={id}
            onClick={onClick}
            loading={state.loadingNext}
            disabled={!isValid}
        >
          {advanceText}
        </Button>
    );
  }

  const determineNextBuyButton = () => {
    const isStillQuote = (model.data.typeCd.toLowerCase() === 'quote');
    if (currentStep === (steps.length - 1)) {
      if (currentNavStep.key === 'payment') {
        sendGoogleAnalyticsEvent(GA_CONFIGURATION.Issuance,true);
        return (mainNavButton(applicationAdvanceText, async () =>
             await paymentRequest(model, setState).then(() => scrollTo(0, 0))));
      } else if (endFlowInd) {
        sendGoogleAnalyticsEvent(GA_CONFIGURATION.Submission,true);
        return (mainNavButton(applicationReferText, () =>
            onSubmitAgent(resourceId, applicationNumber)));
      }
    }
    if (currentNavStep.key === 'policy') {
      const buttonAction = (endFlowInd && isStillQuote ? bindAndNavigate : next);
      return (mainNavButton(applicationAdvanceText, buttonAction));
    }else if (currentNavStep.actionRequest === 'Prefill') {
      const buttonAction = driverDataReportNavigate ;
      return (mainNavButton('Next', buttonAction, 'nextButton'));
    } else {
      mainAction = next;
    }
    return <BuyNextButton />;
  };

  const [progressBarPosition, setProgressBarPosition] = useState('static');

  const handleScroll = () => {
    const scrollPosition = window.scrollY;
    if (scrollPosition >= 171 && progressBarPosition === 'static') {
      return setProgressBarPosition('fixed');
    }
    if (scrollPosition < 171 && progressBarPosition === 'fixed') {
      return setProgressBarPosition('static');
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const buttonActionText = steps[currentStep].buttonActionText;
  const nextButton = determineNextBuyButton( );  // called here to set state
  return (
    <NavigationContext.Provider value={subNavigation}>
      <BuyContext.Provider
        value={{
          steps,
          state,
          setState,
          paymentRequest,
          navigateToStep,
          currentStep,
          isValid,
          next,
          moveTo,
          bindAndNavigate,
          mainAction,
        }}
      >
        <Modal
          visible={state.paymentLoading}
          onCancel={() => null}
          footer={null}
          closeIcon={() => null}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignContent: 'center',
              padding: '32px',
            }}
          >
            <Spin
              indicator={antIcon}
              style={{
                zIndex: 8,
              }}
            />
            <div style={{ marginTop: '16px' }}>
              <p>
                Please wait while we process your transaction. This could take a
                few moments.
              </p>
            </div>
          </div>
        </Modal>
        {isDesktop ? (
          <div
            style={{
              marginRight: '-40px',
              marginLeft: '-40px',
              marginBottom: '36px',
            }}
          >
            <Summary />
          </div>
        ) : (
          <>
            <HorizontalProgressBar
              isMobile={isMobile}
              hideNavLabels={hideNavLabels}
              currentStep={currentStep}
              moveTo={moveTo}
              lineCd={lineCd}
            />
            <div
              style={{
                paddingLeft: hideNavLabels ? '10px' : '40px',
                paddingRight: hideNavLabels ? '10px' : '40px',
              }}
            >
              <Summary onSubmitEmail={onSubmitEmail} />
            </div>
          </>
        )}
        <div
          {...props}
          style={{
            paddingLeft: hideNavLabels ? '10px' : '40px',
            paddingRight: hideNavLabels ? '10px' : '40px',
          }}
        >
          <Row>
            <Col span={isDesktop ? 6 : 24}>
              <div style={{ marginRight: isDesktop ? '40px' : '' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: `${progressBarPosition}`,
                    top: progressBarPosition === 'fixed' ? 8 : '',
                    width: '260px',
                  }}
                >
                  {isDesktop && (
                    <ProgressBar moveTo={moveTo} currentStep={currentStep} lineCd={lineCd}/>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      marginTop: '24px',
                      justifyContent: 'center',
                    }}
                  >
                    {shouldDisplayEmail && emailAddr && givenName && surname && isDesktop && (
                      <GenericButton
                        icon={state.loadingEmail ? null : 'mail'}
                        filled="filled"
                        label={emailQuoteText}
                        onClick={() => {
                          saveApplication().then(()=>{
                            onSubmitEmail()
                          });
                        }
                        }
                        className="secondary-button"
                        style={{
                          width: '189px',
                          height: '40px',
                          fontSize: '18px',
                        }}
                        loading={state.loadingEmail}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      marginTop: '24px',
                      justifyContent: 'center',
                    }}
                  >
                    {shouldDisplayEmail &&
                      shouldDisplayAgentButton &&
                      isDesktop && givenName && surname && (
                        <GenericButton
                          icon="question-circle"
                          filled="filled"
                          label={referAgentText}
                          onClick={() => {
                            model.data.basicPolicy.comments = "Help";
                            saveApplication().then(()=>{
                              onSubmitAgent(resourceId, applicationNumber)
                            });
                          }
                          }
                          className="secondary-button"
                          style={{
                            width: '189px',
                            height: '40px',
                            fontSize: '18px',
                          }}
                        />
                      )}
                  </div>
                </div>
              </div>
            </Col>
            <Col span={isDesktop ? 14:24}>
            {model.notifications.length > 0 && (
              <Col span={24}>
                {model.notifications.map((notification) => (
                  <InlineNotification key={notification.id} {...notification} />
                ))}
              </Col>
            )}
            <Col span={24}>
              {env.DEBUG && (
                <Alert
                  message=""
                  description={JSON.stringify(formStatuses)}
                  type="info"
                />
              )}

              {showAlert && stepKey === 'payment' ? (
                <div>
                  <Alert
                    message=""
                    description={<div><strong>Attention: </strong>{message}</div>}
                    type="info"
                    onClose={()=>{
                      setShowAlert(false);
                    }}
                    showIcon closable
                  />
                </div>
              ) : null}

              {model.dataIssues.all.length ? (
                <div className={'alert-Error-Container'}>
                  <Alert
                    message=""
                    description={model.dataIssues.all.map((issue, i) => (
                      <div key={i}>{JSON.stringify(issue)}</div>
                    ))}
                    type="error"
                  />
                </div>
              ) : null}

              <Card>
                <Row>
                  <Col span={24}>
                    <ErrorScope
                      errorMessage={state.emailQuoteErrors}
                      hasError={true}
                    >
                      {state.loadingNext ||
                      state.loadingBack ||
                      model.locked ? (
                        <Spin
                          indicator={antIcon}
                          style={{
                            top: '50%',
                            left: '50%',
                            position: 'absolute',
                            zIndex: 3,
                          }}
                        />
                      ) : null}
                      {isDesktop ? (
                        <div>
                          <DeclaredComponent declaration={step.content} />
                        </div>
                      ) : (
                        <div style={{ padding: isTablet ? '20px' : '10px' }}>
                          <DeclaredComponent declaration={step.content} />
                        </div>
                      )}
                    </ErrorScope>
                  </Col>
                </Row>

                <Col span={24}>
                  <Divider />
                </Col>

                <Row>
                  <Col span={12}>
                    <Button
                      type="link"
                      disabled={disabled}
                      onClick={onCancelClick}
                      id={'cancelButton'}
                      loading={state.loadingCancel}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col
                    span={12}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      flexDisplay: 'row',
                    }}
                  >
                    {currentStep !== 0 && (
                      <Button
                        onClick={back}
                        loading={state.loadingBack}
                        disabled={disabled}
                        className="secondary-button"
                        style={{ marginRight: '4px' }}
                        id={'previousButton'}
                      >
                        Back
                      </Button>
                    )}
                    {nextButton}
                  </Col>
                </Row>
                {buttonActionText ? <Row>
                  <ActionDescription style={{marginTop:'20px',textAlign:'right'}} text={buttonActionText}/>
                </Row> : null}
              </Card>
            </Col>
            </Col>

            {isDesktop && (
              <Col span={4}>
                <div />
              </Col>
            )}
          </Row>
        </div>
      </BuyContext.Provider>
    </NavigationContext.Provider>
  );
}
